import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useReplyDraftsGateway } from './ReplyDraftsGatewayProvider';
import { toReplyDraftError } from '../api/errors';
import { getReplyDraftV2QueryOptions } from '../api/queryOptions';
import { applyLocalEvents } from '../model/applyLocalEvent';
import { isUnusableDraftError, type ReplyDraftError } from '../model/errors';
import { OPEN_REPLY_DRAFTS_QUERY_KEY } from '../model/queryKeys';
import type { ClientEvent, DraftSelection, ReplyDraft } from '../model/types';

import { Sentry } from '@/util/sentry';

/** Quiet time after the last tap before a batch is sent. */
export const FLUSH_DELAY_MS = 600;

/** Tries of one batch before the taps in it are given up and the server's view restored. */
export const MAX_BATCH_ATTEMPTS = 3;

const RETRYABLE = new Set<ReplyDraftError['kind']>(['network', 'timeout', 'unknown']);

type Queue = {
  /** Sent and not yet answered. */
  inflight: ClientEvent[];
  /** Tapped since the last batch left. */
  pending: ClientEvent[];
};

const EMPTY_QUEUE: Queue = { inflight: [], pending: [] };

export type DraftEditingCallbacks = {
  /** The draft changed elsewhere; the screen now shows the server's version. */
  onConflict?: () => void;
  /** The draft can no longer be used: gone, opted out, or the inquiry closed. */
  onUnusable?: (error: ReplyDraftError) => void;
  /** A change could not be saved and was undone. */
  onFailed?: (error: ReplyDraftError) => void;
};

export type DraftBusy = 'select' | 'note' | null;

/**
 * One draft as the expert edits it.
 *
 * Taps show at once: the draft returned is the server's last view with every
 * unsaved tap folded over it. They reach the server in batches, after a short
 * quiet spell or when `flush` is awaited, one batch at a time, each carrying
 * the state version of the last response.
 *
 * - A stale version means the draft changed elsewhere. The server's current
 *   draft replaces the view, the unsaved taps are dropped rather than replayed
 *   onto a draft the expert has not seen, and `onConflict` fires.
 * - A draft that can no longer be used refreshes the open drafts, so `.draft`
 *   stops being offered, and `onUnusable` fires.
 * - A batch that gets no answer is resent unchanged, which is safe because the
 *   server applies a repeated batch once. After `MAX_BATCH_ATTEMPTS` the taps
 *   are dropped and `onFailed` fires.
 *
 * `select` and `addNote` flush first, so they always carry the latest version.
 * Each resolves to whether it succeeded; failures go to the callbacks.
 */
export function useDraftEditing(replyDraftId: string, callbacks: DraftEditingCallbacks = {}) {
  const gateway = useReplyDraftsGateway();

  const queryClient = useQueryClient();

  const options = useMemo(
    () => getReplyDraftV2QueryOptions(replyDraftId, gateway),
    [replyDraftId, gateway]
  );

  /**
   * Read fresh each time the draft opens. The cache never goes stale while the
   * expert edits, because every write returns the draft, but between openings
   * the asker can write again or another device can edit, and a cached copy
   * would hide the case-updated notice and the other device's changes.
   */
  const query = useQuery({ ...options, refetchOnMount: 'always' });

  const queueRef = useRef<Queue>(EMPTY_QUEUE);

  const [queue, setQueue] = useState<Queue>(EMPTY_QUEUE);

  const [busy, setBusy] = useState<DraftBusy>(null);

  const flightRef = useRef<Promise<boolean> | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const callbacksRef = useRef(callbacks);

  useLayoutEffect(() => {
    callbacksRef.current = callbacks;
  });

  const { queryKey } = options;

  const setQueueState = useCallback((next: Queue) => {
    queueRef.current = next;

    setQueue(next);
  }, []);

  const readServer = useCallback(
    () => queryClient.getQueryData<ReplyDraft>(queryKey),
    [queryClient, queryKey]
  );

  const writeServer = useCallback(
    (draft: ReplyDraft) => queryClient.setQueryData(queryKey, draft),
    [queryClient, queryKey]
  );

  const fail = useCallback(
    (error: ReplyDraftError) => {
      setQueueState(EMPTY_QUEUE);

      if (error.kind === 'stale_state' && error.current) {
        writeServer(error.current);

        callbacksRef.current.onConflict?.();

        return;
      }

      if (isUnusableDraftError(error)) {
        queryClient
          .invalidateQueries({ queryKey: OPEN_REPLY_DRAFTS_QUERY_KEY })
          .catch(() => undefined);

        callbacksRef.current.onUnusable?.(error);

        return;
      }

      Sentry.captureException(error, {
        tags: { component: 'useDraftEditing', source: 'reply-draft' },
        extra: { errorMessage: error.message, kind: error.kind, replyDraftId },
      });

      callbacksRef.current.onFailed?.(error);
    },
    [setQueueState, writeServer, queryClient, replyDraftId]
  );

  const sendBatch = useCallback(async (): Promise<boolean> => {
    const server = readServer();

    const events = queueRef.current.pending;

    if (!server || events.length === 0) {
      return true;
    }

    setQueueState({ inflight: events, pending: [] });

    for (let attempt = 1; ; attempt += 1) {
      try {
        writeServer(await gateway.applyEvents(replyDraftId, server.stateVersion, events));

        setQueueState({ ...queueRef.current, inflight: [] });

        return true;
      } catch (thrown) {
        const error = toReplyDraftError(thrown);

        if (RETRYABLE.has(error.kind) && attempt < MAX_BATCH_ATTEMPTS) {
          continue;
        }

        fail(error);

        return false;
      }
    }
  }, [readServer, setQueueState, writeServer, gateway, replyDraftId, fail]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);

      timerRef.current = null;
    }
  }, []);

  /** Sends every unsaved tap, one batch at a time. Resolves to whether all were saved. */
  const flush = useCallback(async (): Promise<boolean> => {
    clearTimer();

    while (flightRef.current || queueRef.current.pending.length > 0) {
      if (!flightRef.current) {
        flightRef.current = sendBatch().finally(() => {
          flightRef.current = null;
        });
      }

      if (!(await flightRef.current)) {
        return false;
      }
    }

    return true;
  }, [clearTimer, sendBatch]);

  const tap = useCallback(
    (event: ClientEvent) => {
      setQueueState({ ...queueRef.current, pending: [...queueRef.current.pending, event] });

      clearTimer();

      timerRef.current = setTimeout(() => {
        flush().catch(() => undefined);
      }, FLUSH_DELAY_MS);
    },
    [setQueueState, clearTimer, flush]
  );

  const write = useCallback(
    async (kind: Exclude<DraftBusy, null>, run: (stateVersion: number) => Promise<ReplyDraft>) => {
      if (!(await flush())) {
        return false;
      }

      const server = readServer();

      if (!server) {
        return false;
      }

      setBusy(kind);

      try {
        writeServer(await run(server.stateVersion));

        return true;
      } catch (thrown) {
        fail(toReplyDraftError(thrown));

        return false;
      } finally {
        setBusy(null);
      }
    },
    [flush, readServer, writeServer, fail]
  );

  const select = useCallback(
    (selection: DraftSelection) =>
      write('select', (stateVersion) => gateway.select(replyDraftId, stateVersion, selection)),
    [write, gateway, replyDraftId]
  );

  const addNote = useCallback(
    (text: string) =>
      write('note', (stateVersion) => gateway.addNote(replyDraftId, stateVersion, text)),
    [write, gateway, replyDraftId]
  );

  useEffect(
    () => () => {
      if (queueRef.current.pending.length > 0) {
        flush().catch(() => undefined);
      }

      clearTimer();
    },
    [flush, clearTimer]
  );

  const draft = useMemo(() => {
    const server = query.data;

    const unsaved = [...queue.inflight, ...queue.pending];

    if (!server?.draft || unsaved.length === 0) {
      return server;
    }

    return { ...server, draft: applyLocalEvents(server.draft, unsaved) };
  }, [query.data, queue]);

  return {
    draft,
    query,
    busy,
    hasUnsaved: queue.inflight.length + queue.pending.length > 0,
    tap,
    flush,
    select,
    addNote,
  };
}
